import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FaBriefcase, FaBuilding } from "react-icons/fa";
import SectionHeader from "./SectionHeader";
import { fadeUpItem, staggerFast, viewportOnce } from "../motionPresets";

const WorkExperience = () => {
  const reduced = useReducedMotion();
  const role = {
    company: "R-Tech Solutions",
    title: "Full Stack Developer — Software Engineering Internship",
    period: "Nov 2024 – May 2026",
    location: "Sri Lanka",
    highlights: [
      {
        name: "HRM System",
        detail:
          "Backend with Node.js (Express), MySQL, and GraphQL for employee records, attendance, and payroll.",
      },
      {
        name: "Admin Panel",
        detail:
          "Centralized business tool: React.js, Tailwind, Axios on the frontend with a Node.js / GraphQL backend.",
      },
      {
        name: "SDSS Corporate Accounting",
        detail:
          "Real-time financial reporting with React / Redux and Node.js (Fastify) / MySQL / GraphQL.",
      },
      {
        name: "FMMS Marketing Management",
        detail:
          "Campaign automation with Next.js (TypeScript) / Redux and NestJS / MySQL / GraphQL.",
      },
      {
        name: "Learning Management System (LMS)",
        detail:
          "Responsive learner dashboard built with Next.js, Tailwind, and Redux.",
      },
    ],
  };

  return (
    <div
      name="career"
      className="w-full border-t border-white/[0.06] bg-[#030712] text-white pt-20 pb-20 px-4 sm:px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_50%_at_50%_0%,rgba(56,189,248,0.06),transparent)] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <SectionHeader
          eyebrow="Career"
          title="Professional experience"
          subtitle="Hands-on delivery across ERP-style systems, internal tooling, and learner-facing products — with emphasis on maintainable architecture and clear APIs."
        />

        <div className="max-w-3xl mx-auto relative">
          <div className="absolute left-[13px] md:left-[17px] top-2 bottom-8 w-px bg-gradient-to-b from-sky-500/60 via-sky-500/20 to-transparent" />

          <motion.article
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: reduced ? 0 : 0.4 }}
            className="relative pl-11 md:pl-14"
          >
            <div className="absolute left-0 top-1.5 w-7 h-7 rounded-full bg-slate-950 border-2 border-sky-400 shadow-[0_0_0_4px_rgba(15,23,42,0.9),0_0_20px_rgba(56,189,248,0.35)]" />

            <div className="rounded-2xl border border-white/[0.08] bg-slate-900/40 backdrop-blur-sm p-6 md:p-8 shadow-xl shadow-black/30 ring-1 ring-white/[0.04]">
              <div className="flex flex-col gap-6 mb-8 pb-8 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 text-sky-500 text-[11px] font-semibold uppercase tracking-[0.18em]">
                  <FaBriefcase className="text-sm opacity-90" />
                  Current role
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white tracking-tight leading-snug">
                    {role.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-3 text-slate-400 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-slate-300">
                      <FaBuilding className="text-sky-500/90 text-xs" />
                      {role.company}
                    </span>
                    <span className="text-slate-600">·</span>
                    <span>{role.period}</span>
                    <span className="text-slate-600 hidden sm:inline">·</span>
                    <span className="text-slate-500">{role.location}</span>
                  </div>
                </div>
              </div>

              <motion.ul
                className="space-y-6"
                variants={staggerFast}
                initial={reduced ? false : "hidden"}
                animate={reduced ? "visible" : undefined}
                whileInView={reduced ? undefined : "visible"}
                viewport={viewportOnce}
              >
                {role.highlights.map((item) => (
                  <motion.li key={item.name} className="group" variants={fadeUpItem}>
                    <div className="flex gap-4">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400 ring-4 ring-sky-400/10 group-hover:ring-sky-400/20 transition-all" />
                      <div>
                        <h4 className="font-semibold text-sky-200/95 text-[15px] tracking-tight group-hover:text-sky-100 transition-colors">
                          {item.name}
                        </h4>
                        <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mt-1.5">
                          {item.detail}
                        </p>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </motion.article>
        </div>
      </div>
    </div>
  );
};

export default WorkExperience;
